import * as turf from '@turf/turf';
import type { Feature, FeatureCollection, Geometry, MultiPolygon, Polygon } from 'geojson';

// CG-482 — client-side geometry validation for the sub-geometry draw/edit flow. These are pure,
// turf-only helpers (no React, no UI imports) so they can be unit-tested in the node-env client
// vitest. They mirror the server-side invariants (CG-473) closely enough to give Civil Search fast
// in-UI feedback; the API stays the source of truth (it re-checks and is authoritative).

// Mirrors the server-side SUB_GEOMETRY_CONTAINMENT_THRESHOLD: warn when less than this fraction of
// the drawn area falls inside the agency's service area.
export const SUB_GEOMETRY_CONTAINMENT_THRESHOLD = 0.95;

// Edge-sharing (adjacent) polygons can produce a hair-thin sliver intersection from floating-point
// math. Treat anything below ~1 m² as boundary-only contact (allowed), matching the server's
// interior-overlap rule ST_Relate(a, b, '2********') where shared edges don't count (CG-473).
const OVERLAP_AREA_EPSILON_SQ_METERS = 1;

type PolyGeometry = Polygon | MultiPolygon;
export type SubGeometryLike = { id: string; geometry: Geometry };

function toPolygonFeature(geometry: Geometry | null | undefined): Feature<PolyGeometry> | null {
  if (!geometry || (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon')) return null;
  return turf.feature(geometry as PolyGeometry);
}

/**
 * Returns the ids of `existing` sub-geometries whose interior overlaps `drawn` (i.e. they share 2D
 * area). Adjacent (edge-only) polygons are NOT flagged, and full containment in either direction IS
 * flagged — mirroring the server's overlap check so the UI can block + highlight the conflict
 * before the request round-trips.
 */
export function findOverlappingSubGeometryIds(drawn: PolyGeometry, existing: SubGeometryLike[]): string[] {
  const drawnFeature = toPolygonFeature(drawn);
  if (!drawnFeature) return [];

  const ids: string[] = [];
  for (const sg of existing) {
    const sgFeature = toPolygonFeature(sg.geometry);
    if (!sgFeature) continue;

    let shared: Feature<PolyGeometry> | null = null;
    try {
      shared = turf.intersect(turf.featureCollection([drawnFeature, sgFeature]));
    } catch {
      // turf can throw on degenerate/self-intersecting input; fall back to "no overlap" and let the
      // server (authoritative) reject it if it really overlaps.
      shared = null;
    }
    if (shared && turf.area(shared) > OVERLAP_AREA_EPSILON_SQ_METERS) ids.push(sg.id);
  }
  return ids;
}

/**
 * Fraction (0..1) of `drawn` that falls inside the agency `footprint`, or `null` when there's
 * nothing meaningful to compare against (empty footprint or zero-area drawn geometry). Mirrors the
 * server's equal-area `computeContainmentFraction` closely enough for fast UI feedback.
 */
export function computeContainedFraction(drawn: PolyGeometry, footprint: FeatureCollection): number | null {
  const drawnFeature = toPolygonFeature(drawn);
  if (!drawnFeature) return null;
  const drawnArea = turf.area(drawnFeature);
  if (drawnArea <= 0) return null;

  const footprintPolys = (footprint.features ?? []).filter(
    (f): f is Feature<PolyGeometry> => f.geometry?.type === 'Polygon' || f.geometry?.type === 'MultiPolygon',
  );
  if (footprintPolys.length === 0) return null;

  let footprintUnion: Feature<PolyGeometry> | null;
  try {
    footprintUnion =
      footprintPolys.length === 1 ? footprintPolys[0] : turf.union(turf.featureCollection(footprintPolys));
  } catch {
    return null;
  }
  if (!footprintUnion) return null;

  let inside: Feature<PolyGeometry> | null = null;
  try {
    inside = turf.intersect(turf.featureCollection([drawnFeature, footprintUnion]));
  } catch {
    return null;
  }

  const insideArea = inside ? turf.area(inside) : 0;
  return Math.min(1, insideArea / drawnArea);
}

/**
 * Warn-only out-of-bounds check (never blocks): true when a meaningful fraction of the drawn area
 * falls outside the agency footprint. Mirrors the server's `buildContainmentWarning` gate (CG-473).
 */
export function isOutOfBounds(fraction: number | null): boolean {
  return fraction !== null && fraction < SUB_GEOMETRY_CONTAINMENT_THRESHOLD;
}
